import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function tooltipContentFor(label, price) {
  return price ? `${label} · ${price}` : label;
}

/**
 * Mapa con uno o varios marcadores.
 * - Uso simple (un punto): <LeafletMap lat={..} lng={..} label={..} price={..} />
 * - Uso múltiple (varios coches): <LeafletMap markers={[{ lat, lng, label, price }, ...]} />
 */
export default function LeafletMap({ lat = 40.3662, lng = -3.5937, zoom = 13, label = "Ubicación", price = null, markers = null, height = 320 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);

  const points = markers && markers.length > 0
    ? markers
    : [{ lat, lng, label, price }];

  const pointsKey = JSON.stringify(points.map(p => [p.lat, p.lng, p.label, p.price]));

  useEffect(() => {
    if (!ref.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { zoomControl: true }).setView([points[0].lat, points[0].lng], zoom);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(mapRef.current);
      markersLayerRef.current = L.layerGroup().addTo(mapRef.current);
    }

    const layer = markersLayerRef.current;
    layer.clearLayers();

    const validPoints = points.filter(p => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)));
    validPoints.forEach(p => {
      L.marker([p.lat, p.lng])
        .addTo(layer)
        .bindTooltip(tooltipContentFor(p.label, p.price), { permanent: true, direction: "top", offset: [0, -30] });
    });

    if (validPoints.length === 1) {
      mapRef.current.setView([validPoints[0].lat, validPoints[0].lng], zoom);
    } else if (validPoints.length > 1) {
      const bounds = L.latLngBounds(validPoints.map(p => [p.lat, p.lng]));
      mapRef.current.fitBounds(bounds, { padding: [30, 30] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey, zoom]);

  return <div ref={ref} style={{ height, width: "100%", borderRadius: 8 }} data-testid="leaflet-map" />;
}
